import { getMediaInputActionValue } from "../media-action-value.js";
import { HotkeyCoordinator } from "./coordinator.js";
import { isMacOS } from "@videojs/utils/dom";
//#region src/dom/hotkey/hotkey.ts
const MODIFIER_KEYS = /* @__PURE__ */ new Set([
	"shift",
	"ctrl",
	"alt",
	"meta"
]);
/**
* Parse a key pattern string into one or more bindings.
*
* @example
*   ```ts
*   parseHotkeyPattern('>');
*   // [{ modifiers: Set(), key: '>', originalKey: '>' }]
*
*   parseHotkeyPattern('0-9');
*   // 10 bindings, one per digit
*   ```;
*/
function parseHotkeyPattern(pattern) {
	if (pattern === "0-9") return Array.from({ length: 10 }, (_, i) => ({
		modifiers: /* @__PURE__ */ new Set(),
		key: String(i),
		originalKey: String(i)
	}));
	const segments = pattern.split("+");
	const rawKey = segments.pop();
	const modifiers = /* @__PURE__ */ new Set();
	for (const seg of segments) {
		const lower = seg.toLowerCase();
		if (lower === "mod") modifiers.add(isMacOS() ? "meta" : "ctrl");
		else if (MODIFIER_KEYS.has(lower)) modifiers.add(lower);
		else console.warn(`[vjs-hotkey] Unknown modifier: "${seg}" in pattern "${pattern}"`);
	}
	return [{
		modifiers,
		key: rawKey === "Space" ? " " : rawKey.toLowerCase(),
		originalKey: rawKey
	}];
}
/**
* Single non-letter character — layout-dependent modifiers (Shift, Alt/Option) were used to produce the character
* itself, not as deliberate modifiers (e.g. Shift+. → ">", Option+Shift → ">" on some Mac layouts). Letters excluded
* because Shift changes case intentionally (k vs K). Named keys excluded because event.key.length > 1 (ArrowLeft, Tab,
* etc.).
*/
function isImplicitModifierKey(key) {
	return key.length === 1 && !/[a-z]/i.test(key);
}
/** Whether a parsed binding matches a keyboard event. */
function matchesHotkeyEvent(binding, event) {
	if (event.key === "Unidentified") return false;
	if (event.key.toLowerCase() !== binding.key) return false;
	const implicit = isImplicitModifierKey(event.key);
	const shiftKey = implicit ? event.shiftKey && binding.modifiers.has("shift") : event.shiftKey;
	const altKey = implicit ? event.altKey && binding.modifiers.has("alt") : event.altKey;
	if (shiftKey !== binding.modifiers.has("shift")) return false;
	if (event.ctrlKey !== binding.modifiers.has("ctrl")) return false;
	if (altKey !== binding.modifiers.has("alt")) return false;
	if (event.metaKey !== binding.modifiers.has("meta")) return false;
	return true;
}
const coordinators = /* @__PURE__ */ new WeakMap();
/** Look up the coordinator for a target element, if one exists. */
function findHotkeyCoordinator(target) {
	return coordinators.get(target);
}
/** Look up or create the hotkey coordinator for a target element. */
function getHotkeyCoordinator(target) {
	let coordinator = coordinators.get(target);
	if (!coordinator) {
		coordinator = new HotkeyCoordinator(target);
		coordinators.set(target, coordinator);
	}
	return coordinator;
}
/**
* Register a hotkey binding on a target element.
*
* @example
*   ```ts
*   const cleanup = createHotkey(container, {
*     keys: 'k',
*     onActivate: () => (store.paused ? store.play() : store.pause()),
*   });
*
*   // Later: remove the binding
*   cleanup();
*   ```;
*
* @returns A cleanup function that removes the binding.
*/
function createHotkey(target, options) {
	const coordinator = getHotkeyCoordinator(target);
	const key = parseHotkeyPattern(options.keys)[0]?.originalKey;
	return coordinator.add({
		...options,
		value: getMediaInputActionValue(options.action ?? "", key, options.value)
	});
}
//#endregion
export { createHotkey, findHotkeyCoordinator, getHotkeyCoordinator, matchesHotkeyEvent, parseHotkeyPattern };

//# sourceMappingURL=hotkey.js.map